// ============================================================================
// Checklists — data layer (migration 089, checklist_items)
// ----------------------------------------------------------------------------
// One list per estimate or job. PQ builds and prepares items through pq-chat
// (create_checklist / draft_checklist_email / prepare_portal_answers); the
// contractor checks them off, edits them, and adds his own here. Nothing in
// this file sends anything: "Open in email" hands a draft to the device's
// mail app, and the contractor presses Send himself. (Sending on approval is
// the Command track's Approvals inbox — one approval system.)
// Reads/writes go straight through tenant RLS.
// ============================================================================
import { supabase } from '@/lib/supabaseClient';

export const CHECKLIST_KINDS = ['email', 'portal', 'homeowner', 'internal'];
export const CHECKLIST_STATUSES = ['todo', 'drafted', 'waiting', 'done', 'skipped'];

const COLUMNS =
  'id,scope,scope_id,title,kind,status,owner,due_date,waiting_on,notes,' +
  'recipient_name,recipient_email,draft_subject,draft_body,portal_url,' +
  'portal_answers,sort,source,created_at,updated_at,done_at';

const isMissingTable = (error) =>
  error?.code === '42P01' || /does not exist|could not find the/i.test(error?.message || '');

const scopeOf = ({ estimateId, jobId }) =>
  estimateId ? { scope: 'estimate', scopeId: estimateId } : jobId ? { scope: 'job', scopeId: jobId } : null;

/**
 * The checklist for one estimate or job, in list order. Resolves
 * { available: false } until migration 089 is applied so the panel hides
 * instead of erroring.
 */
export const fetchChecklist = async ({ estimateId = null, jobId = null } = {}) => {
  const s = scopeOf({ estimateId, jobId });
  if (!s) return { available: true, rows: [] };
  const { data, error } = await supabase
    .from('checklist_items')
    .select(COLUMNS)
    .eq('scope', s.scope)
    .eq('scope_id', s.scopeId)
    .order('sort', { ascending: true })
    .order('created_at', { ascending: true })
    .limit(200);
  if (error) {
    if (isMissingTable(error)) return { available: false, rows: [] };
    throw error;
  }
  return { available: true, rows: data || [] };
};

/** Add a manual item at the end of the list. contractor_id is stamped by trigger. */
export const addChecklistItem = async ({ estimateId = null, jobId = null, title, kind = 'internal', sort = 0 }) => {
  const s = scopeOf({ estimateId, jobId });
  if (!s) throw new Error('No estimate or job to attach this to.');
  const clean = String(title || '').trim().slice(0, 200);
  if (clean.length < 2) throw new Error('Give the item a title.');
  const { data, error } = await supabase
    .from('checklist_items')
    .insert({
      scope: s.scope,
      scope_id: s.scopeId,
      title: clean,
      kind: CHECKLIST_KINDS.includes(kind) ? kind : 'internal',
      sort,
      source: 'manual',
    })
    .select(COLUMNS)
    .single();
  if (error) throw error;
  return data;
};

export const updateChecklistItem = async (id, patch) => {
  const { data, error } = await supabase
    .from('checklist_items')
    .update(patch)
    .eq('id', id)
    .select(COLUMNS)
    .single();
  if (error) throw error;
  return data;
};

export const deleteChecklistItem = async (id) => {
  const { error } = await supabase.from('checklist_items').delete().eq('id', id);
  if (error) throw error;
};

// Long bodies get silently truncated by some mail apps when passed in a
// mailto link (and very long URLs fail to open at all) — past this, the
// link carries To + Subject only and the body goes to the clipboard.
export const MAILTO_BODY_LIMIT = 1800;

/**
 * Build the mailto link for a drafted email item.
 * Returns { href, bodyInLink } — when bodyInLink is false the caller should
 * copy the body to the clipboard and tell the contractor to paste it.
 */
export const buildMailto = ({ to = '', subject = '', body = '' } = {}) => {
  const addr = /^[^\s@<>,;]+@[^\s@<>,;]+\.[^\s@<>,;]+$/.test(String(to).trim()) ? String(to).trim() : '';
  const params = [];
  if (subject) params.push(`subject=${encodeURIComponent(subject)}`);
  const encodedBody = body ? encodeURIComponent(body) : '';
  const bodyInLink = !!encodedBody && encodedBody.length <= MAILTO_BODY_LIMIT;
  if (bodyInLink) params.push(`body=${encodedBody}`);
  const href = `mailto:${addr ? encodeURIComponent(addr).replace(/%40/g, '@') : ''}${params.length ? `?${params.join('&')}` : ''}`;
  return { href, bodyInLink: bodyInLink || !body };
};

/** Portal answers come back as jsonb — normalize to [{field, value, source}]. */
export const portalAnswersOf = (item) =>
  (Array.isArray(item?.portal_answers) ? item.portal_answers : [])
    .filter((a) => a && typeof a === 'object' && String(a.field || '').trim())
    .map((a) => ({
      field: String(a.field).trim(),
      value: a.value == null ? '' : String(a.value),
      source: a.source == null ? '' : String(a.source),
    }));

/** Answers the records didn't have — shown as "needs: …" for him to fill. */
export const isNeedsAnswer = (a) => !String(a?.value || '').trim();

/** Open items first (in list order), then done/skipped at the bottom. */
export const sortChecklist = (rows) => {
  const closed = (r) => r.status === 'done' || r.status === 'skipped';
  return [...(rows || [])].sort((a, b) => {
    if (closed(a) !== closed(b)) return closed(a) ? 1 : -1;
    if ((a.sort || 0) !== (b.sort || 0)) return (a.sort || 0) - (b.sort || 0);
    return String(a.created_at || '').localeCompare(String(b.created_at || ''));
  });
};
